import * as Blockly from 'blockly';
import { javascriptGenerator } from 'blockly/javascript';
import { BLOCKLY_COLORS } from './blocky-types.types';
import { FieldChevron } from './field-chevron';

/**
 * Helper: Gets code from a value input, handling both template-based generators
 * (return plain string) and custom generators (return [code, order] tuple).
 */
export function getValueCode(block: any, inputName: string): string {
  const child = block.getInputTargetBlock(inputName);
  if (!child) return '';
  const result = javascriptGenerator.blockToCode(child);
  if (Array.isArray(result)) return result[0] || '';
  return result || '';
}

const BUTTON_SIZE = 20;
const BUTTON_PADDING = 2;

/**
 * Custom field that renders a clickable + or - button on a Blockly block.
 */
export class FieldPlusMinus extends Blockly.Field<string> {
  private foreignObject_: SVGForeignObjectElement | null = null;
  private buttonElement_: HTMLElement | null = null;
  private isPlus_: boolean;

  static override fromJson(options: Blockly.FieldConfig & { isPlus?: boolean }): FieldPlusMinus {
    return new FieldPlusMinus(options.isPlus ?? true);
  }

  constructor(isPlus: boolean) {
    super(isPlus ? '+' : '-');
    this.isPlus_ = isPlus;
    this.SERIALIZABLE = true;
    (this as any).CURSOR = 'pointer';
  }

  override initView(): void {
    this.createBorderRect_();
    this.createButton_();
    this.updateSize_();
  }

  private createButton_(): void {
    if (!this.fieldGroup_) return;

    const fo = document.createElementNS('http://www.w3.org/2000/svg', 'foreignObject');
    fo.setAttribute('width', `${BUTTON_SIZE + BUTTON_PADDING * 2}`);
    fo.setAttribute('height', `${BUTTON_SIZE + BUTTON_PADDING * 2}`);
    fo.setAttribute('y', '0');

    const wrapper = document.createElement('div');
    wrapper.style.display = 'flex';
    wrapper.style.alignItems = 'center';
    wrapper.style.justifyContent = 'center';
    wrapper.style.width = '100%';
    wrapper.style.height = '100%';
    wrapper.style.cursor = 'pointer';
    wrapper.style.userSelect = 'none';

    const btn = document.createElement('div');
    btn.style.width = `${BUTTON_SIZE - 4}px`;
    btn.style.height = `${BUTTON_SIZE - 4}px`;
    btn.style.borderRadius = '50%';
    btn.style.backgroundColor = this.isPlus_ ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.25)';
    btn.style.display = 'flex';
    btn.style.alignItems = 'center';
    btn.style.justifyContent = 'center';
    btn.style.fontSize = '14px';
    btn.style.fontWeight = 'bold';
    btn.style.color = 'rgba(255,255,255,0.9)';
    btn.style.lineHeight = '1';
    btn.textContent = this.isPlus_ ? '+' : '−';
    btn.title = this.isPlus_ ? 'Eingang hinzufügen' : 'Eingang entfernen';

    wrapper.appendChild(btn);
    fo.appendChild(wrapper);
    this.fieldGroup_.appendChild(fo);

    this.foreignObject_ = fo;
    this.buttonElement_ = btn;
  }

  protected override updateSize_(): void {
    const totalWidth = BUTTON_SIZE + BUTTON_PADDING * 2;
    const totalHeight = BUTTON_SIZE + BUTTON_PADDING * 2;

    this.size_ = new Blockly.utils.Size(totalWidth, totalHeight);

    if (this.borderRect_) {
      this.borderRect_.setAttribute('width', `${totalWidth}`);
      this.borderRect_.setAttribute('height', `${totalHeight}`);
      this.borderRect_.style.fill = 'transparent';
      this.borderRect_.style.stroke = 'none';
    }
  }

  override showEditor_(): void {
    const block = this.getSourceBlock();
    if (!block) return;
    if (this.isPlus_) {
      (block as any).addInput_();
    } else {
      (block as any).removeInput_();
    }
  }
}

Blockly.fieldRegistry.register('field_plus_minus', FieldPlusMinus as any);

/**
 * Mixin for the text_join_plus_minus mutator.
 * Manages dynamic ADD inputs with + and - buttons.
 */
const TextJoinPlusMinusMixin: any = {
  inputCount_: 2,

  /**
   * Create the block shape: title row with +/- buttons, then N value inputs.
   */
  updateShape_: function (): void {
    // Remove the buttons row if it exists
    if (this.getInput('BUTTONS')) {
      this.removeInput('BUTTONS');
    }

    // Remove excess inputs
    let i = this.inputCount_;
    while (this.getInput('ADD' + i)) {
      this.removeInput('ADD' + i);
      i++;
    }

    // Add missing inputs
    for (let j = 0; j < this.inputCount_; j++) {
      if (!this.getInput('ADD' + j)) {
        this.appendValueInput('ADD' + j);
      }
    }

    // Add buttons row at the end
    const buttonsInput = this.appendDummyInput('BUTTONS');
    buttonsInput.appendField(new FieldPlusMinus(true), 'PLUS');
    buttonsInput.appendField(new FieldPlusMinus(false), 'MINUS');

    // Switch to external inputs when 3+ elements for better readability
    this.setInputsInline(this.inputCount_ < 3);
  },

  addInput_: function (): void {
    this.inputCount_++;
    this.updateShape_();
    if (this.rendered) {
      this.render();
      this.bumpNeighbours();
    }
  },

  removeInput_: function (): void {
    if (this.inputCount_ > 1) {
      // Disconnect any block connected to the last input
      const lastInput = this.getInput('ADD' + (this.inputCount_ - 1));
      if (lastInput && lastInput.connection && lastInput.connection.targetBlock()) {
        lastInput.connection.targetBlock().unplug(true);
      }
      this.inputCount_--;
      this.updateShape_();
      if (this.rendered) {
        this.render();
        this.bumpNeighbours();
      }
    }
  },

  saveExtraState: function (): any {
    return { inputCount: this.inputCount_ };
  },

  loadExtraState: function (state: any): void {
    this.inputCount_ = state?.inputCount ?? 2;
    this.updateShape_();
  },

  mutationToDom: function (): Element {
    const mutation = Blockly.utils.xml.createElement('mutation');
    mutation.setAttribute('items', String(this.inputCount_));
    return mutation;
  },

  domToMutation: function (xmlElement: Element): void {
    const items = xmlElement.getAttribute('items');
    this.inputCount_ = items ? parseInt(items, 10) : 2;
    this.updateShape_();
  },
};

let _registered = false;

/**
 * Registers the text_join_plus_minus block and its code generator.
 * Call once before the block is used.
 */
export function registerTextJoinPlusMinus(): void {
  if (_registered) return;
  _registered = true;

  // Register the mutator extension
  Blockly.Extensions.registerMutator(
    'text_join_plus_minus_mutator',
    TextJoinPlusMinusMixin,
    undefined,
    []
  );

  // Define the block
  Blockly.defineBlocksWithJsonArray([
    {
      type: 'text_join_plus_minus',
      message0: 'Texte verbinden',
      args0: [],
      output: 'String',
      inputsInline: true,
      colour: BLOCKLY_COLORS.MESSAGES,
      tooltip: 'Erstelle einen Text, indem du beliebig viele Elemente verbindest.',
      mutator: 'text_join_plus_minus_mutator',
    }
  ]);

  // Override init to set up the initial shape
  const originalInit = Blockly.Blocks['text_join_plus_minus'].init;
  Blockly.Blocks['text_join_plus_minus'].init = function (this: any) {
    originalInit.call(this);
    this.inputCount_ = 2;
    this.updateShape_();
  };

  // Code generator: same logic as text_join
  javascriptGenerator.forBlock['text_join_plus_minus'] = function (block) {
    const elements: string[] = [];
    let i = 0;
    while (block.getInput('ADD' + i)) {
      const code = getValueCode(block, 'ADD' + i);
      if (!code) {
        elements.push('""');
      } else {
        elements.push(code);
      }
      i++;
    }
    return [elements.join(' + '), 0];
  };
}



/**
 * Mixin for the CARD_BUTTONS_NEW mutator.
 * Manages dynamic BUTTON inputs with + and - buttons, plus a collapsible CSS input.
 */
const CardButtonsPlusMinusMixin: any = {
  inputCount_: 1,
  _cssExpanded: false,
  _cssInitialized: false,

  updateShape_: function (): void {
    // Remove dynamic rows (not CSS_INPUT which is persistent)
    if (this.getInput('BUTTONS_ROW')) {
      this.removeInput('BUTTONS_ROW');
    }

    let i = this.inputCount_;
    while (this.getInput('ADD' + i)) {
      this.removeInput('ADD' + i);
      i++;
    }

    // CSS input is created once and stays at the top (after message0)
    if (!this._cssInitialized) {
      this._cssInitialized = true;
      const cssInput = this.appendValueInput('CSS_INPUT');
      cssInput.appendField('CSS-Class:');
    }

    for (let j = 0; j < this.inputCount_; j++) {
      if (!this.getInput('ADD' + j)) {
        this.appendValueInput('ADD' + j).setCheck('BUTTON');
      }
    }

    const buttonsInput = this.appendDummyInput('BUTTONS_ROW');
    buttonsInput.appendField(new FieldPlusMinus(true), 'PLUS');
    buttonsInput.appendField(new FieldPlusMinus(false), 'MINUS');
    buttonsInput.appendField(new FieldChevron(this._cssExpanded), 'CSS_TOGGLE');

    this._applyCssVisibility();
  },

  _applyCssVisibility: function (): void {
    const cssInput = this.getInput('CSS_INPUT');
    if (cssInput) {
      cssInput.setVisible(this._cssExpanded);
    }
  },

  toggleHiddenFields: function (): void {
    this._cssExpanded = !this._cssExpanded;
    this._applyCssVisibility();

    const chevronField = this.getField('CSS_TOGGLE');
    if (chevronField) {
      (chevronField as any).setExpanded(this._cssExpanded);
    }

    if (this.rendered) {
      this.render();
      this.bumpNeighbours();
    }
  },

  addInput_: function (): void {
    this.inputCount_++;
    this.updateShape_();
    if (this.rendered) {
      this.render();
      this.bumpNeighbours();
    }
  },

  removeInput_: function (): void {
    if (this.inputCount_ > 1) {
      const lastInput = this.getInput('ADD' + (this.inputCount_ - 1));
      if (lastInput && lastInput.connection && lastInput.connection.targetBlock()) {
        lastInput.connection.targetBlock().unplug(true);
      }
      this.inputCount_--;
      this.updateShape_();
      if (this.rendered) {
        this.render();
        this.bumpNeighbours();
      }
    }
  },

  saveExtraState: function (): any {
    return { inputCount: this.inputCount_, cssExpanded: this._cssExpanded };
  },

  loadExtraState: function (state: any): void {
    this.inputCount_ = state?.inputCount ?? 1;
    this._cssExpanded = state?.cssExpanded ?? false;
    this.updateShape_();
  },

  mutationToDom: function (): Element {
    const mutation = Blockly.utils.xml.createElement('mutation');
    mutation.setAttribute('items', String(this.inputCount_));
    mutation.setAttribute('css_expanded', String(this._cssExpanded));
    return mutation;
  },

  domToMutation: function (xmlElement: Element): void {
    const items = xmlElement.getAttribute('items');
    this.inputCount_ = items ? parseInt(items, 10) : 1;
    this._cssExpanded = xmlElement.getAttribute('css_expanded') === 'true';
    this.updateShape_();
  },
};

let _cardButtonsRegistered = false;

/**
 * Registers the CARD_BUTTONS_NEW block with +/- for BUTTON inputs.
 * Call once before the block is used.
 */
export function registerCardButtonsNew(): void {
  if (_cardButtonsRegistered) return;
  _cardButtonsRegistered = true;

  Blockly.Extensions.registerMutator(
    'card_buttons_plus_minus_mutator',
    CardButtonsPlusMinusMixin,
    undefined,
    []
  );

  Blockly.defineBlocksWithJsonArray([
    {
      type: 'CARD_BUTTONS_NEW',
      message0: '%1 Buttons im Chat',
      args0: [
        { type: 'field_icon', name: 'ICON', icon: 'ri-toggle-line' }
      ],
      inputsInline: false,
      previousStatement: null,
      nextStatement: null,
      colour: BLOCKLY_COLORS.BUTTONS,
      tooltip: 'Zeigt Buttons im Chat an.',
      mutator: 'card_buttons_plus_minus_mutator',
    }
  ]);

  const originalInit = Blockly.Blocks['CARD_BUTTONS_NEW'].init;
  Blockly.Blocks['CARD_BUTTONS_NEW'].init = function (this: any) {
    originalInit.call(this);
    this.inputCount_ = 1;
    this.updateShape_();
  };

  javascriptGenerator.forBlock['CARD_BUTTONS_NEW'] = function (block) {
    const elements: string[] = [];
    let i = 0;
    while (block.getInput('ADD' + i)) {
      const code = getValueCode(block, 'ADD' + i);
      if (code) {
        elements.push(code);
      }
      i++;
    }
    const css = getValueCode(block, 'CSS_INPUT') || '""';
    return `await RAGAI.CARDS.buttons([${elements.join(', ')}].filter(Boolean),${css});\n`;
  };
}


/**
 * Mixin for the SKILL_FOR_NEXT_RUN_NEW mutator.
 * Manages dynamic SKILL inputs with + and - buttons directly on the action block.
 */
const SkillForNextRunMixin: any = {
  inputCount_: 1,

  updateShape_: function (): void {
    if (this.getInput('BUTTONS_ROW')) {
      this.removeInput('BUTTONS_ROW');
    }

    let i = this.inputCount_;
    while (this.getInput('ADD' + i)) {
      this.removeInput('ADD' + i);
      i++;
    }

    for (let j = 0; j < this.inputCount_; j++) {
      if (!this.getInput('ADD' + j)) {
        this.appendValueInput('ADD' + j).setCheck('SKILL');
      }
    }

    const buttonsInput = this.appendDummyInput('BUTTONS_ROW');
    buttonsInput.appendField(new FieldPlusMinus(true), 'PLUS');
    buttonsInput.appendField(new FieldPlusMinus(false), 'MINUS');
  },

  addInput_: function (): void {
    this.inputCount_++;
    this.updateShape_();
    if (this.rendered) {
      this.render();
      this.bumpNeighbours();
    }
  },

  removeInput_: function (): void {
    if (this.inputCount_ > 1) {
      const lastInput = this.getInput('ADD' + (this.inputCount_ - 1));
      if (lastInput && lastInput.connection && lastInput.connection.targetBlock()) {
        lastInput.connection.targetBlock().unplug(true);
      }
      this.inputCount_--;
      this.updateShape_();
      if (this.rendered) {
        this.render();
        this.bumpNeighbours();
      }
    }
  },

  saveExtraState: function (): any {
    return { inputCount: this.inputCount_ };
  },

  loadExtraState: function (state: any): void {
    this.inputCount_ = state?.inputCount ?? 1;
    this.updateShape_();
  },

  mutationToDom: function (): Element {
    const mutation = Blockly.utils.xml.createElement('mutation');
    mutation.setAttribute('items', String(this.inputCount_));
    return mutation;
  },

  domToMutation: function (xmlElement: Element): void {
    const items = xmlElement.getAttribute('items');
    this.inputCount_ = items ? parseInt(items, 10) : 1;
    this.updateShape_();
  },
};

let _skillForNextRunRegistered = false;

/**
 * Registers the SKILL_FOR_NEXT_RUN_NEW block with +/- for SKILL inputs.
 */
export function registerSkillForNextRunNew(): void {
  if (_skillForNextRunRegistered) return;
  _skillForNextRunRegistered = true;

  Blockly.Extensions.registerMutator(
    'skill_for_next_run_plus_minus_mutator',
    SkillForNextRunMixin,
    undefined,
    []
  );

  Blockly.defineBlocksWithJsonArray([
    {
      type: 'SKILL_FOR_NEXT_RUN_NEW',
      message0: 'Skills für die nächste Bot-Antwort erzwingen:',
      args0: [],
      inputsInline: false,
      previousStatement: null,
      nextStatement: null,
      colour: BLOCKLY_COLORS.SKILLS,
      tooltip: 'Erzwingt bestimmte Skills für die nächste Bot-Antwort.',
      mutator: 'skill_for_next_run_plus_minus_mutator',
    }
  ]);

  const originalInit = Blockly.Blocks['SKILL_FOR_NEXT_RUN_NEW'].init;
  Blockly.Blocks['SKILL_FOR_NEXT_RUN_NEW'].init = function (this: any) {
    originalInit.call(this);
    this.inputCount_ = 1;
    this.updateShape_();
  };

  javascriptGenerator.forBlock['SKILL_FOR_NEXT_RUN_NEW'] = function (block) {
    const elements: string[] = [];
    let i = 0;
    while (block.getInput('ADD' + i)) {
      const code = getValueCode(block, 'ADD' + i);
      if (code) {
        elements.push(code);
      }
      i++;
    }
    return `await RAGAI.SKILL.skillsForNextRun([${elements.join(', ')}]);\n`;
  };
}


/**
 * Mixin for the RAGAI_BUTTON_OUTPUT_NEW mutator.
 * Button block with collapsible "Vorbedingung" (condition) via chevron toggle.
 */
const ButtonOutputMixin: any = {
  _conditionExpanded: false,
  _conditionInitialized: false,

  updateShape_: function (): void {
    // Remove chevron row to re-add at the end
    if (this.getInput('CHEVRON_ROW')) {
      this.removeInput('CHEVRON_ROW');
    }

    // Create hidden fields once
    if (!this._conditionInitialized) {
      this._conditionInitialized = true;
      const condInput = this.appendValueInput('CONDITION');
      condInput.setCheck('Boolean');
      condInput.appendField('Vorbedingung:');

      const cssInput = this.appendValueInput('CSS_INPUT');
      cssInput.appendField('CSS-Klasse:');
    }

    // Add chevron row at the end
    const chevronInput = this.appendDummyInput('CHEVRON_ROW');
    chevronInput.appendField(new FieldChevron(this._conditionExpanded), 'CONDITION_TOGGLE');

    this._applyConditionVisibility();
  },

  _applyConditionVisibility: function (): void {
    const condInput = this.getInput('CONDITION');
    if (condInput) {
      condInput.setVisible(this._conditionExpanded);
    }
    const cssInput = this.getInput('CSS_INPUT');
    if (cssInput) {
      cssInput.setVisible(this._conditionExpanded);
    }
  },

  toggleHiddenFields: function (): void {
    this._conditionExpanded = !this._conditionExpanded;
    this._applyConditionVisibility();

    const chevronField = this.getField('CONDITION_TOGGLE');
    if (chevronField) {
      (chevronField as any).setExpanded(this._conditionExpanded);
    }

    if (this.rendered) {
      this.render();
      this.bumpNeighbours();
    }
  },

  saveExtraState: function (): any {
    return { conditionExpanded: this._conditionExpanded };
  },

  loadExtraState: function (state: any): void {
    this._conditionExpanded = state?.conditionExpanded ?? false;
    this.updateShape_();
  },

  mutationToDom: function (): Element {
    const mutation = Blockly.utils.xml.createElement('mutation');
    mutation.setAttribute('condition_expanded', String(this._conditionExpanded));
    return mutation;
  },

  domToMutation: function (xmlElement: Element): void {
    this._conditionExpanded = xmlElement.getAttribute('condition_expanded') === 'true';
    this.updateShape_();
  },
};

let _buttonOutputRegistered = false;

/**
 * Registers the RAGAI_BUTTON_OUTPUT_NEW block with chevron-toggleable condition.
 */
export function registerButtonOutputNew(): void {
  if (_buttonOutputRegistered) return;
  _buttonOutputRegistered = true;

  Blockly.Extensions.registerMutator(
    'button_output_condition_mutator',
    ButtonOutputMixin,
    undefined,
    []
  );

  Blockly.defineBlocksWithJsonArray([
    {
      type: 'RAGAI_BUTTON_OUTPUT_NEW',
      message0: '%1 Button: %2  bei klick: %3',
      args0: [
        { type: 'field_icon', name: 'ICON', icon: 'ri-toggle-line' },
        { type: 'input_value', name: 'TEXT', check: 'String' },
        { type: 'input_statement', name: 'ACTION', check: ['ACTION', 'OVERLAY'] }
      ],
      output: 'BUTTON',
      inputsInline: true,
      colour: BLOCKLY_COLORS.BUTTONS,
      tooltip: 'Button mit optionaler Vorbedingung (Chevron öffnen).',
      mutator: 'button_output_condition_mutator',
    }
  ]);

  const originalInit = Blockly.Blocks['RAGAI_BUTTON_OUTPUT_NEW'].init;
  Blockly.Blocks['RAGAI_BUTTON_OUTPUT_NEW'].init = function (this: any) {
    originalInit.call(this);
    this._conditionExpanded = false;
    this._conditionInitialized = false;
    this.updateShape_();
  };

  javascriptGenerator.forBlock['RAGAI_BUTTON_OUTPUT_NEW'] = function (block) {
    const text = getValueCode(block, 'TEXT') || '""';
    const action = javascriptGenerator.statementToCode(block, 'ACTION') || '';

    let cssProperty = '';
    const cssCode = getValueCode(block, 'CSS_INPUT');
    if (cssCode) {
      cssProperty = `, className:${cssCode}`;
    }

    const buttonObj = `{text:${text}${cssProperty},\n      onClick: async function(){\n      RAGAI.EVENTS.trigger("RAGAI.BUTTON.CLICK", {text: ${text}});\n      ${action}\n      }}`;

    // Check if condition is connected
    const condCode = getValueCode(block, 'CONDITION');
    if (condCode) {
      return [`(${condCode} ? ${buttonObj} : null)`, 0];
    }
    return [buttonObj, 0];
  };
}


/**
 * Mixin for the ANSWER_OPTIONS_NEW mutator.
 * Manages dynamic String inputs with + and - buttons for answer options.
 */
const AnswerOptionsPlusMinusMixin: any = {
  inputCount_: 1,

  updateShape_: function (): void {
    if (this.getInput('BUTTONS_ROW')) {
      this.removeInput('BUTTONS_ROW');
    }

    let i = this.inputCount_;
    while (this.getInput('ADD' + i)) {
      this.removeInput('ADD' + i);
      i++;
    }

    for (let j = 0; j < this.inputCount_; j++) {
      if (!this.getInput('ADD' + j)) {
        this.appendValueInput('ADD' + j).setCheck('String');
      }
    }

    const buttonsInput = this.appendDummyInput('BUTTONS_ROW');
    buttonsInput.appendField(new FieldPlusMinus(true), 'PLUS');
    buttonsInput.appendField(new FieldPlusMinus(false), 'MINUS');
  },

  addInput_: function (): void {
    this.inputCount_++;
    this.updateShape_();
    if (this.rendered) {
      this.render();
      this.bumpNeighbours();
    }
  },

  removeInput_: function (): void {
    if (this.inputCount_ > 1) {
      const lastInput = this.getInput('ADD' + (this.inputCount_ - 1));
      if (lastInput && lastInput.connection && lastInput.connection.targetBlock()) {
        lastInput.connection.targetBlock().unplug(true);
      }
      this.inputCount_--;
      this.updateShape_();
      if (this.rendered) {
        this.render();
        this.bumpNeighbours();
      }
    }
  },

  saveExtraState: function (): any {
    return { inputCount: this.inputCount_ };
  },

  loadExtraState: function (state: any): void {
    this.inputCount_ = state?.inputCount ?? 1;
    this.updateShape_();
  },

  mutationToDom: function (): Element {
    const mutation = Blockly.utils.xml.createElement('mutation');
    mutation.setAttribute('items', String(this.inputCount_));
    return mutation;
  },

  domToMutation: function (xmlElement: Element): void {
    const items = xmlElement.getAttribute('items');
    this.inputCount_ = items ? parseInt(items, 10) : 1;
    this.updateShape_();
  },
};

let _answerOptionsRegistered = false;

/**
 * Registers the ANSWER_OPTIONS_NEW block with +/- for answer option inputs.
 */
export function registerAnswerOptionsNew(): void {
  if (_answerOptionsRegistered) return;
  _answerOptionsRegistered = true;

  Blockly.Extensions.registerMutator(
    'answer_options_plus_minus_mutator',
    AnswerOptionsPlusMinusMixin,
    undefined,
    []
  );

  Blockly.defineBlocksWithJsonArray([
    {
      type: 'ANSWER_OPTIONS_NEW',
      message0: '%1 Antwortoptionen anbieten',
      args0: [
        { type: 'field_icon', name: 'ICON', icon: 'ri-toggle-line' }
      ],
      inputsInline: false,
      previousStatement: null,
      nextStatement: null,
      colour: BLOCKLY_COLORS.BUTTONS,
      tooltip: 'Bietet dem Nutzer Antwortoptionen als klickbare Links an.',
      mutator: 'answer_options_plus_minus_mutator',
    }
  ]);

  const originalInit = Blockly.Blocks['ANSWER_OPTIONS_NEW'].init;
  Blockly.Blocks['ANSWER_OPTIONS_NEW'].init = function (this: any) {
    originalInit.call(this);
    this.inputCount_ = 1;
    this.updateShape_();
  };

  javascriptGenerator.forBlock['ANSWER_OPTIONS_NEW'] = function (block) {
    const elements: string[] = [];
    let i = 0;
    while (block.getInput('ADD' + i)) {
      const code = getValueCode(block, 'ADD' + i);
      if (code) {
        elements.push('"[[" + ' + code + ' + "]]"');
      }
      i++;
    }
    return `await RAGAI.MSG.botMessage(${elements.join(' + ')});\n`;
  };
}


/**
 * Mixin for the OVERLAY_TEXT_BTN_NEW mutator.
 * Manages a TITLE value input, a collapsible PLACEMENT dropdown, and dynamic BUTTON inputs with +/- buttons.
 */
const OverlayTextBtnPlusMinusMixin: any = {
  inputCount_: 1,
  _placementExpanded: false,
  _placementInitialized: false,

  updateShape_: function (): void {
    if (this.getInput('BUTTONS_ROW')) {
      this.removeInput('BUTTONS_ROW');
    }

    let i = this.inputCount_;
    while (this.getInput('ADD' + i)) {
      this.removeInput('ADD' + i);
      i++;
    }

    // Placement input and CSS class input are created once and stay
    if (!this._placementInitialized) {
      this._placementInitialized = true;
      const placementInput = this.appendDummyInput('PLACEMENT_INPUT');
      placementInput.appendField('Ort:');
      placementInput.appendField(new Blockly.FieldDropdown([
        ['Über Chat', "'botview'"],
        ['Über Fusionview', "'fusionview'"]
      ]), 'PLACEMENT');

      const cssInput = this.appendValueInput('CSS_INPUT');
      cssInput.appendField('CSS-Klasse:');
    }

    for (let j = 0; j < this.inputCount_; j++) {
      if (!this.getInput('ADD' + j)) {
        this.appendValueInput('ADD' + j).setCheck('BUTTON');
      }
    }

    const buttonsInput = this.appendDummyInput('BUTTONS_ROW');
    buttonsInput.appendField(new FieldPlusMinus(true), 'PLUS');
    buttonsInput.appendField(new FieldPlusMinus(false), 'MINUS');
    buttonsInput.appendField(new FieldChevron(this._placementExpanded), 'PLACEMENT_TOGGLE');

    this._applyPlacementVisibility();
  },

  _applyPlacementVisibility: function (): void {
    const placementInput = this.getInput('PLACEMENT_INPUT');
    if (placementInput) {
      placementInput.setVisible(this._placementExpanded);
    }
    const cssInput = this.getInput('CSS_INPUT');
    if (cssInput) {
      cssInput.setVisible(this._placementExpanded);
    }
  },

  toggleHiddenFields: function (): void {
    this._placementExpanded = !this._placementExpanded;
    this._applyPlacementVisibility();

    const chevronField = this.getField('PLACEMENT_TOGGLE');
    if (chevronField) {
      (chevronField as any).setExpanded(this._placementExpanded);
    }

    if (this.rendered) {
      this.render();
      this.bumpNeighbours();
    }
  },

  addInput_: function (): void {
    this.inputCount_++;
    this.updateShape_();
    if (this.rendered) {
      this.render();
      this.bumpNeighbours();
    }
  },

  removeInput_: function (): void {
    if (this.inputCount_ > 1) {
      const lastInput = this.getInput('ADD' + (this.inputCount_ - 1));
      if (lastInput && lastInput.connection && lastInput.connection.targetBlock()) {
        lastInput.connection.targetBlock().unplug(true);
      }
      this.inputCount_--;
      this.updateShape_();
      if (this.rendered) {
        this.render();
        this.bumpNeighbours();
      }
    }
  },

  saveExtraState: function (): any {
    return { inputCount: this.inputCount_, placementExpanded: this._placementExpanded };
  },

  loadExtraState: function (state: any): void {
    this.inputCount_ = state?.inputCount ?? 1;
    this._placementExpanded = state?.placementExpanded ?? false;
    this.updateShape_();
  },

  mutationToDom: function (): Element {
    const mutation = Blockly.utils.xml.createElement('mutation');
    mutation.setAttribute('items', String(this.inputCount_));
    mutation.setAttribute('placement_expanded', String(this._placementExpanded));
    return mutation;
  },

  domToMutation: function (xmlElement: Element): void {
    const items = xmlElement.getAttribute('items');
    this.inputCount_ = items ? parseInt(items, 10) : 1;
    this._placementExpanded = xmlElement.getAttribute('placement_expanded') === 'true';
    this.updateShape_();
  },
};

let _overlayTextBtnRegistered = false;

/**
 * Registers the OVERLAY_TEXT_BTN_NEW block with +/- for BUTTON inputs.
 */
export function registerOverlayTextBtnNew(): void {
  if (_overlayTextBtnRegistered) return;
  _overlayTextBtnRegistered = true;

  Blockly.Extensions.registerMutator(
    'overlay_text_btn_plus_minus_mutator',
    OverlayTextBtnPlusMinusMixin,
    undefined,
    []
  );

  Blockly.defineBlocksWithJsonArray([
    {
      type: 'OVERLAY_TEXT_BTN_NEW',
      message0: '%1 Overlay Text + Buttons %2',
      args0: [
        { type: 'field_icon', name: 'ICON', icon: 'ri-layout-2-line' },
        { type: 'input_value', name: 'TITLE', check: 'String' }
      ],
      inputsInline: false,
      previousStatement: null,
      nextStatement: null,
      colour: BLOCKLY_COLORS.OVERLAYS,
      tooltip: 'Zeigt ein Overlay mit Text und Buttons an.',
      mutator: 'overlay_text_btn_plus_minus_mutator',
    }
  ]);

  const originalInit = Blockly.Blocks['OVERLAY_TEXT_BTN_NEW'].init;
  Blockly.Blocks['OVERLAY_TEXT_BTN_NEW'].init = function (this: any) {
    originalInit.call(this);
    this.inputCount_ = 1;
    this.updateShape_();
  };

  javascriptGenerator.forBlock['OVERLAY_TEXT_BTN_NEW'] = function (block) {
    const elements: string[] = [];
    let i = 0;
    while (block.getInput('ADD' + i)) {
      const code = getValueCode(block, 'ADD' + i);
      if (code) {
        elements.push(code);
      }
      i++;
    }
    const title = getValueCode(block, 'TITLE') || '""';
    const placement = block.getFieldValue('PLACEMENT') || "'botview'";
    const css = getValueCode(block, 'CSS_INPUT') || '""';
    return `await RAGAI.OVERLAY.setPlacement(${placement});\nRAGAI.OVERLAY.textAndButton(${title},[${elements.join(', ')}].filter(Boolean), null, ${css});\n`;
  };
}


/**
 * Mixin for the OVERLAY_PROMPT_NEW mutator.
 * Manages a TITLE value input, a collapsible PLACEMENT dropdown, and dynamic BUTTON inputs with +/- buttons.
 */
const OverlayPromptPlusMinusMixin: any = {
  inputCount_: 2,
  _placementExpanded: false,
  _placementInitialized: false,

  updateShape_: function (): void {
    if (this.getInput('BUTTONS_ROW')) {
      this.removeInput('BUTTONS_ROW');
    }

    let i = this.inputCount_;
    while (this.getInput('ADD' + i)) {
      this.removeInput('ADD' + i);
      i++;
    }

    // Placement input and CSS class input are created once and stay
    if (!this._placementInitialized) {
      this._placementInitialized = true;
      const placementInput = this.appendDummyInput('PLACEMENT_INPUT');
      placementInput.appendField('Ort:');
      placementInput.appendField(new Blockly.FieldDropdown([
        ['Über Chat', "'botview'"],
        ['Über Fusionview', "'fusionview'"]
      ]), 'PLACEMENT');

      const cssInput = this.appendValueInput('CSS_INPUT');
      cssInput.appendField('CSS-Klasse:');
    }

    for (let j = 0; j < this.inputCount_; j++) {
      if (!this.getInput('ADD' + j)) {
        this.appendValueInput('ADD' + j).setCheck('BUTTON');
      }
    }

    const buttonsInput = this.appendDummyInput('BUTTONS_ROW');
    buttonsInput.appendField(new FieldPlusMinus(true), 'PLUS');
    buttonsInput.appendField(new FieldPlusMinus(false), 'MINUS');
    buttonsInput.appendField(new FieldChevron(this._placementExpanded), 'PLACEMENT_TOGGLE');

    this._applyPlacementVisibility();
  },

  _applyPlacementVisibility: function (): void {
    const placementInput = this.getInput('PLACEMENT_INPUT');
    if (placementInput) {
      placementInput.setVisible(this._placementExpanded);
    }
    const cssInput = this.getInput('CSS_INPUT');
    if (cssInput) {
      cssInput.setVisible(this._placementExpanded);
    }
  },

  toggleHiddenFields: function (): void {
    this._placementExpanded = !this._placementExpanded;
    this._applyPlacementVisibility();

    const chevronField = this.getField('PLACEMENT_TOGGLE');
    if (chevronField) {
      (chevronField as any).setExpanded(this._placementExpanded);
    }

    if (this.rendered) {
      this.render();
      this.bumpNeighbours();
    }
  },

  addInput_: function (): void {
    this.inputCount_++;
    this.updateShape_();
    if (this.rendered) {
      this.render();
      this.bumpNeighbours();
    }
  },

  removeInput_: function (): void {
    if (this.inputCount_ > 1) {
      const lastInput = this.getInput('ADD' + (this.inputCount_ - 1));
      if (lastInput && lastInput.connection && lastInput.connection.targetBlock()) {
        lastInput.connection.targetBlock().unplug(true);
      }
      this.inputCount_--;
      this.updateShape_();
      if (this.rendered) {
        this.render();
        this.bumpNeighbours();
      }
    }
  },

  saveExtraState: function (): any {
    return { inputCount: this.inputCount_, placementExpanded: this._placementExpanded };
  },

  loadExtraState: function (state: any): void {
    this.inputCount_ = state?.inputCount ?? 2;
    this._placementExpanded = state?.placementExpanded ?? false;
    this.updateShape_();
  },

  mutationToDom: function (): Element {
    const mutation = Blockly.utils.xml.createElement('mutation');
    mutation.setAttribute('items', String(this.inputCount_));
    mutation.setAttribute('placement_expanded', String(this._placementExpanded));
    return mutation;
  },

  domToMutation: function (xmlElement: Element): void {
    const items = xmlElement.getAttribute('items');
    this.inputCount_ = items ? parseInt(items, 10) : 2;
    this._placementExpanded = xmlElement.getAttribute('placement_expanded') === 'true';
    this.updateShape_();
  },
};

let _overlayPromptRegistered = false;

/**
 * Registers the OVERLAY_PROMPT_NEW block with +/- for BUTTON inputs.
 */
export function registerOverlayPromptNew(): void {
  if (_overlayPromptRegistered) return;
  _overlayPromptRegistered = true;

  Blockly.Extensions.registerMutator(
    'overlay_prompt_plus_minus_mutator',
    OverlayPromptPlusMinusMixin,
    undefined,
    []
  );

  Blockly.defineBlocksWithJsonArray([
    {
      type: 'OVERLAY_PROMPT_NEW',
      message0: '%1 Nutzereingabe %2',
      args0: [
        { type: 'field_icon', name: 'ICON', icon: 'ri-questionnaire-line' },
        { type: 'input_value', name: 'TITLE', check: 'String' }
      ],
      inputsInline: false,
      previousStatement: null,
      nextStatement: null,
      colour: BLOCKLY_COLORS.OVERLAYS,
      tooltip: 'Zeigt ein Overlay mit Nutzereingabe und Buttons an.',
      mutator: 'overlay_prompt_plus_minus_mutator',
    }
  ]);

  const originalInit = Blockly.Blocks['OVERLAY_PROMPT_NEW'].init;
  Blockly.Blocks['OVERLAY_PROMPT_NEW'].init = function (this: any) {
    originalInit.call(this);
    this.inputCount_ = 2;
    this.updateShape_();
  };

  javascriptGenerator.forBlock['OVERLAY_PROMPT_NEW'] = function (block) {
    const elements: string[] = [];
    let i = 0;
    while (block.getInput('ADD' + i)) {
      const code = getValueCode(block, 'ADD' + i);
      if (code) {
        elements.push(code);
      }
      i++;
    }
    const title = getValueCode(block, 'TITLE') || '""';
    const placement = block.getFieldValue('PLACEMENT') || "'botview'";
    const css = getValueCode(block, 'CSS_INPUT') || '""';
    return `await RAGAI.OVERLAY.setPlacement(${placement});\nRAGAI.OVERLAY.prompt(${title},[${elements.join(', ')}].filter(Boolean), null, ${css});\n`;
  };
}


let _overlayCloseRegistered = false;

/**
 * Registers the OVERLAY_CLOSE block.
 */
export function registerOverlayClose(): void {
  if (_overlayCloseRegistered) return;
  _overlayCloseRegistered = true;

  Blockly.defineBlocksWithJsonArray([
    {
      type: 'OVERLAY_CLOSE',
      message0: '%1 Offenes Overlay schließen',
      args0: [
        { type: 'field_icon', name: 'ICON', icon: 'ri-close-circle-line' }
      ],
      inputsInline: false,
      previousStatement: null,
      nextStatement: null,
      colour: BLOCKLY_COLORS.OVERLAYS,
      tooltip: 'Schließt das aktuell offene Overlay.',
    }
  ]);

  javascriptGenerator.forBlock['OVERLAY_CLOSE'] = function () {
    return `await RAGAI.OVERLAY.close();\n`;
  };
}


/**
 * Mixin for the TOASTR_PRECONF_NEW mutator.
 * Shows a notification with type dropdown, title/message inputs,
 * and a chevron toggle for advanced options (progressBar, timeOut, position, etc.)
 */
const ToastrPreconfNewMixin: any = {
  _optionsExpanded: false,
  _optionsInitialized: false,

  updateShape_: function (): void {
    // Options inputs created once and toggled with chevron
    if (!this._optionsInitialized) {
      this._optionsInitialized = true;

      const progressInput = this.appendDummyInput('OPT_PROGRESS');
      progressInput.appendField('Fortschrittsbalken');
      progressInput.appendField(new Blockly.FieldCheckbox('TRUE'), 'PROGRESS_BAR');

      const timeoutInput = this.appendDummyInput('OPT_TIMEOUT');
      timeoutInput.appendField('Automatisch schließen nach');
      timeoutInput.appendField(new Blockly.FieldNumber(5000, 0), 'TIMEOUT');
      timeoutInput.appendField('ms');

      const disableTimeoutInput = this.appendDummyInput('OPT_DISABLE_TIMEOUT');
      disableTimeoutInput.appendField('Nicht automatisch schließen');
      disableTimeoutInput.appendField(new Blockly.FieldCheckbox('FALSE'), 'DISABLE_TIMEOUT');

      const positionInput = this.appendDummyInput('OPT_POSITION');
      positionInput.appendField('Position');
      positionInput.appendField(new Blockly.FieldDropdown([
        ['Oben rechts', 'toast-top-right'],
        ['Oben links', 'toast-top-left'],
        ['Oben mittig', 'toast-top-center'],
        ['Oben volle Breite', 'toast-top-full-width'],
        ['Unten rechts', 'toast-bottom-right'],
        ['Unten links', 'toast-bottom-left'],
        ['Unten mittig', 'toast-bottom-center'],
        ['Unten volle Breite', 'toast-bottom-full-width']
      ]), 'POSITION');

      const tapInput = this.appendDummyInput('OPT_TAP');
      tapInput.appendField('Schließen bei Klick');
      tapInput.appendField(new Blockly.FieldCheckbox('FALSE'), 'TAP_TO_DISMISS');
    }

    // Chevron row - recreate to keep it at the bottom
    if (this.getInput('CHEVRON_ROW')) {
      this.removeInput('CHEVRON_ROW');
    }
    const chevronRow = this.appendDummyInput('CHEVRON_ROW');
    chevronRow.appendField(new FieldChevron(this._optionsExpanded), 'OPTIONS_TOGGLE');

    this._applyOptionsVisibility();
  },

  _applyOptionsVisibility: function (): void {
    const inputs = ['OPT_PROGRESS', 'OPT_TIMEOUT', 'OPT_DISABLE_TIMEOUT', 'OPT_POSITION', 'OPT_TAP'];
    for (const name of inputs) {
      const input = this.getInput(name);
      if (input) {
        input.setVisible(this._optionsExpanded);
      }
    }
  },

  toggleHiddenFields: function (): void {
    this._optionsExpanded = !this._optionsExpanded;
    this._applyOptionsVisibility();

    const chevronField = this.getField('OPTIONS_TOGGLE');
    if (chevronField) {
      (chevronField as any).setExpanded(this._optionsExpanded);
    }

    if (this.rendered) {
      this.render();
      this.bumpNeighbours();
    }
  },

  saveExtraState: function (): any {
    return {
      optionsExpanded: this._optionsExpanded,
      progressBar: this.getFieldValue('PROGRESS_BAR') ?? 'TRUE',
      timeout: this.getFieldValue('TIMEOUT') ?? 5000,
      disableTimeout: this.getFieldValue('DISABLE_TIMEOUT') ?? 'FALSE',
      position: this.getFieldValue('POSITION') ?? 'toast-top-right',
      tapToDismiss: this.getFieldValue('TAP_TO_DISMISS') ?? 'FALSE',
    };
  },

  loadExtraState: function (state: any): void {
    this._optionsExpanded = state?.optionsExpanded ?? false;
    this.updateShape_();
    // Restore field values after inputs are created
    if (state?.progressBar != null) this.setFieldValue(state.progressBar, 'PROGRESS_BAR');
    if (state?.timeout != null) this.setFieldValue(state.timeout, 'TIMEOUT');
    if (state?.disableTimeout != null) this.setFieldValue(state.disableTimeout, 'DISABLE_TIMEOUT');
    if (state?.position != null) this.setFieldValue(state.position, 'POSITION');
    if (state?.tapToDismiss != null) this.setFieldValue(state.tapToDismiss, 'TAP_TO_DISMISS');
  },

  mutationToDom: function (): Element {
    const mutation = Blockly.utils.xml.createElement('mutation');
    mutation.setAttribute('options_expanded', String(this._optionsExpanded));
    mutation.setAttribute('progress_bar', this.getFieldValue('PROGRESS_BAR') ?? 'TRUE');
    mutation.setAttribute('timeout', String(this.getFieldValue('TIMEOUT') ?? 5000));
    mutation.setAttribute('disable_timeout', this.getFieldValue('DISABLE_TIMEOUT') ?? 'FALSE');
    mutation.setAttribute('position', this.getFieldValue('POSITION') ?? 'toast-top-right');
    mutation.setAttribute('tap_to_dismiss', this.getFieldValue('TAP_TO_DISMISS') ?? 'FALSE');
    return mutation;
  },

  domToMutation: function (xmlElement: Element): void {
    this._optionsExpanded = xmlElement.getAttribute('options_expanded') === 'true';
    this.updateShape_();
    const progressBar = xmlElement.getAttribute('progress_bar');
    if (progressBar) this.setFieldValue(progressBar, 'PROGRESS_BAR');
    const timeout = xmlElement.getAttribute('timeout');
    if (timeout) this.setFieldValue(Number(timeout), 'TIMEOUT');
    const disableTimeout = xmlElement.getAttribute('disable_timeout');
    if (disableTimeout) this.setFieldValue(disableTimeout, 'DISABLE_TIMEOUT');
    const position = xmlElement.getAttribute('position');
    if (position) this.setFieldValue(position, 'POSITION');
    const tapToDismiss = xmlElement.getAttribute('tap_to_dismiss');
    if (tapToDismiss) this.setFieldValue(tapToDismiss, 'TAP_TO_DISMISS');
  },
};

let _toastrPreconfNewRegistered = false;

/**
 * Registers the TOASTR_PRECONF_NEW block with chevron for advanced options.
 */
export function registerToastrPreconfNew(): void {
  if (_toastrPreconfNewRegistered) return;
  _toastrPreconfNewRegistered = true;

  Blockly.Extensions.registerMutator(
    'toastr_preconf_new_mutator',
    ToastrPreconfNewMixin,
    undefined,
    []
  );

  Blockly.defineBlocksWithJsonArray([
    {
      type: 'TOASTR_PRECONF_NEW',
      message0: '%1 Titel %2 Nachricht %3',
      args0: [
        {
          type: 'field_dropdown', name: 'MODE',
          options: [
            ['✅ Erfolg', 'success'],
            ['❌ Fehler', 'error'],
            ['ℹ️ Info', 'info'],
            ['⚠️ Warnung', 'warning']
          ]
        },
        { type: 'input_value', name: 'TITLE', check: 'String' },
        { type: 'input_value', name: 'MSG', check: 'String' }
      ],
      inputsInline: false,
      previousStatement: null,
      nextStatement: null,
      colour: BLOCKLY_COLORS.TOASTR,
      tooltip: 'Zeigt eine Benachrichtigung (Toast) im Chat an.',
      mutator: 'toastr_preconf_new_mutator',
    }
  ]);

  const originalInit = Blockly.Blocks['TOASTR_PRECONF_NEW'].init;
  Blockly.Blocks['TOASTR_PRECONF_NEW'].init = function (this: any) {
    originalInit.call(this);
    this._optionsExpanded = false;
    this._optionsInitialized = false;
    this.updateShape_();
  };

  javascriptGenerator.forBlock['TOASTR_PRECONF_NEW'] = function (block: any) {
    const mode = block.getFieldValue('MODE') || 'success';
    const title = getValueCode(block, 'TITLE') || '""';
    const msg = getValueCode(block, 'MSG') || '""';
    const progressBar = block.getFieldValue('PROGRESS_BAR') === 'TRUE';
    const timeout = block.getFieldValue('TIMEOUT') || 5000;
    const disableTimeout = block.getFieldValue('DISABLE_TIMEOUT') === 'TRUE';
    const position = block.getFieldValue('POSITION') || 'toast-top-right';
    const tapToDismiss = block.getFieldValue('TAP_TO_DISMISS') === 'TRUE';

    const options: string[] = [];
    if (progressBar) options.push('progressBar: true');
    if (disableTimeout) {
      options.push('disableTimeOut: true');
    } else if (timeout !== 5000) {
      options.push(`timeOut: ${timeout}`);
    }
    if (position !== 'toast-top-right') options.push(`positionClass: '${position}'`);
    if (!tapToDismiss) options.push('tapToDismiss: false');

    const optionsStr = options.length > 0 ? `, { ${options.join(', ')} }` : '';
    return `RAGAI.toastr.${mode}(${title}, ${msg}${optionsStr});\n`;
  };
}


/**
 * Mixin for the DOC_SEARCH_NEW_V2 mutator.
 * Manages dynamic DOCUMENT inputs with + and - buttons directly on the search block.
 */
const DocSearchNewV2Mixin: any = {
  docCount_: 1,

  updateShape_: function (): void {
    if (this.getInput('BUTTONS_ROW')) {
      this.removeInput('BUTTONS_ROW');
    }

    let i = this.docCount_;
    while (this.getInput('DOC' + i)) {
      this.removeInput('DOC' + i);
      i++;
    }

    for (let j = 0; j < this.docCount_; j++) {
      if (!this.getInput('DOC' + j)) {
        const input = this.appendValueInput('DOC' + j).setCheck('DOCUMENT');
        if (j === 0) {
          input.appendField('Folgende Dokumente sollen durchsucht werden:\n');
        }
      }
    }

    const buttonsInput = this.appendDummyInput('BUTTONS_ROW');
    buttonsInput.appendField(new FieldPlusMinus(true), 'PLUS');
    buttonsInput.appendField(new FieldPlusMinus(false), 'MINUS');
  },

  addInput_: function (): void {
    this.docCount_++;
    this.updateShape_();
    if (this.rendered) {
      this.render();
      this.bumpNeighbours();
    }
  },

  removeInput_: function (): void {
    if (this.docCount_ > 1) {
      const lastInput = this.getInput('DOC' + (this.docCount_ - 1));
      if (lastInput && lastInput.connection && lastInput.connection.targetBlock()) {
        lastInput.connection.targetBlock().unplug(true);
      }
      this.docCount_--;
      this.updateShape_();
      if (this.rendered) {
        this.render();
        this.bumpNeighbours();
      }
    }
  },

  saveExtraState: function (): any {
    return { docCount: this.docCount_ };
  },

  loadExtraState: function (state: any): void {
    this.docCount_ = state?.docCount ?? 1;
    this.updateShape_();
  },

  mutationToDom: function (): Element {
    const mutation = Blockly.utils.xml.createElement('mutation');
    mutation.setAttribute('items', String(this.docCount_));
    return mutation;
  },

  domToMutation: function (xmlElement: Element): void {
    const items = xmlElement.getAttribute('items');
    this.docCount_ = items ? parseInt(items, 10) : 1;
    this.updateShape_();
  },
};

let _docSearchNewV2Registered = false;

/**
 * Registers the DOC_SEARCH_NEW_V2 block with +/- for DOCUMENT inputs directly integrated.
 * Output: DOC_SEARCH_JSON
 */
export function registerDocSearchNewV2(): void {
  if (_docSearchNewV2Registered) return;
  _docSearchNewV2Registered = true;

  Blockly.Extensions.registerMutator(
    'doc_search_v2_plus_minus_mutator',
    DocSearchNewV2Mixin,
    undefined,
    []
  );

  Blockly.defineBlocksWithJsonArray([
    {
      type: 'DOC_SEARCH_NEW_V2',
      message0: 'Information aus Dokumentenabfrage\n Typ: %1 \n Suche nach: %2   Min. Übereinstimmung: %4 % \n Anzahl Suchtreffer: %3 \n Suchtyp: %5 \n ',
      args0: [
        {
          type: 'field_dropdown',
          name: 'RESULTTYPE',
          options: [
            ['⎇ Liefert die Suche Ergebnisse?', 'hasresults'],
            ['Ergebnisse als Tabelle', 'table'],
            ['Ergebnisse als Text', 'text'],
            ['🔢 Anzahl der Ergebnisse', 'counter'],
            ['⌗ Suchantwort als JSON-Objekt', 'json']
          ],
        },
        { type: 'input_value', name: 'SEARCHSTRING', check: 'String' },
        { type: 'field_number', name: 'SEARCHCOUNT', value: 5, min: 1 },
        { type: 'field_number', name: 'MINSCORE', value: 79, min: 0, max: 100 },
        {
          type: 'field_dropdown',
          name: 'SEARCHTYPE',
          options: [
            ['🔍 Vektor', 'vector'],
            ['📝 Volltext', 'text'],
            ['⚡ Kombiniert', 'combined']
          ],
        }
      ],
      output: 'DOC_SEARCH_JSON',
      inputsInline: false,
      colour: BLOCKLY_COLORS.CAT_DATEN_INHALTE,
      tooltip: 'Vektor: Semantische Suche über KI-Embeddings – findet inhaltlich Ähnliches auch ohne exakte Stichwörter. Schnellste Methode. Volltext: Keyword-Suche mit Fuzzy-Matching – treffsicher bei exakten Begriffen, kein semantisches Verständnis. Langsamer als Vektor. Kombiniert: Vereint beide Methoden für beste Ergebnisqualität – am langsamsten. Empfehlung: Ausprobieren, welche Methode die besten Ergebnisse liefert.',
      mutator: 'doc_search_v2_plus_minus_mutator',
    }
  ]);

  const originalInit = Blockly.Blocks['DOC_SEARCH_NEW_V2'].init;
  Blockly.Blocks['DOC_SEARCH_NEW_V2'].init = function (this: any) {
    originalInit.call(this);
    this.docCount_ = 1;
    this.updateShape_();
    // Set shadow state for SEARCHSTRING and DOC0
    const searchInput = this.getInput('SEARCHSTRING');
    if (searchInput?.connection) {
      searchInput.connection.setShadowState({ type: 'DUMMY_STRING', fields: { text: 'Suchbegriff' } });
    }
    const docInput = this.getInput('DOC0');
    if (docInput?.connection) {
      docInput.connection.setShadowState({ type: 'BLOCK_STACK_DOCUMENT' });
    }
  };

  javascriptGenerator.forBlock['DOC_SEARCH_NEW_V2'] = function (block) {
    const searchstring = getValueCode(block, 'SEARCHSTRING') || "''";
    const resulttype = block.getFieldValue('RESULTTYPE') || 'hasresults';
    const searchcount = block.getFieldValue('SEARCHCOUNT') || 5;
    const minscore = block.getFieldValue('MINSCORE') || 79;
    const searchtype = block.getFieldValue('SEARCHTYPE') || 'vector';

    const docs: string[] = [];
    let i = 0;
    while (block.getInput('DOC' + i)) {
      const code = getValueCode(block, 'DOC' + i);
      if (code) {
        docs.push(code);
      }
      i++;
    }

    const docList = `[${docs.join(', ')}]`;
    return [`await RAGAI.DOCSEARCH.searchAsJson(${searchstring}, ${docList}, ${searchcount}, ${minscore}, '${resulttype}', '${searchtype}')`, 0];
  };
}
